import { LIMITS } from "@checklist/shared";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBanner, fieldCls, labelCls } from "./AuthShell.js";
import { Button } from "./ui/Button.js";
import { Modal } from "./ui/Modal.js";
import { showToast } from "./ui/Toast.js";
import { ApiError } from "../lib/api.js";
import { humanizeApiError } from "../lib/apiErrors.js";
import { suggestedChecklistName } from "../lib/checklistName.js";

export function StartChecklistModal({
  open,
  sourceName,
  onClose,
  onStart,
}: {
  open: boolean;
  sourceName: string;
  onClose: () => void;
  onStart: (name: string) => Promise<{ id: string; name: string }>;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(suggestedChecklistName(sourceName));
    setError(null);
    setSaving(false);
  }, [open, sourceName]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await onStart(trimmed);
      showToast(`Started "${created.name}"`);
      onClose();
      navigate(`/checklists/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(humanizeApiError(err, "Failed to start checklist"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="start-checklist-title">
      <h2 id="start-checklist-title" className="text-lg font-semibold text-ink">
        Start a checklist
      </h2>
      <p className="mt-1 text-sm text-muted">
        This creates a checklist you can check off. Your lists and templates stay as they are.
      </p>
      <form className="mt-4 space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <ErrorBanner message={error} />
        <div>
          <label htmlFor="startChecklistName" className={labelCls}>
            Name
          </label>
          <input
            id="startChecklistName"
            className={fieldCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={LIMITS.nameMax}
            autoFocus
            required
          />
        </div>
        <Button type="submit" fullWidth disabled={saving}>
          {saving ? "Starting…" : "Start a checklist"}
        </Button>
      </form>
    </Modal>
  );
}
